import { Alert, Button, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useAtomValue } from 'jotai/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { campaignAtom, sharedApi, useCampaigns } from '@data-access';
import { OsImportForm } from '@ui';

export const Route = createFileRoute('/_app/os/import')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const [opened, actions] = useDisclosure();
  const campaignId = useAtomValue(campaignAtom);
  const campaigns = useCampaigns();

  const campaignYear = useMemo(() => {
    const selected = campaigns.data?.find(
      (campaign) => campaign.id === campaignId,
    );
    if (selected) {
      return selected.year;
    }
    return campaigns.data?.find(
      (campaign) => campaign.year === new Date().getFullYear(),
    )?.year;
  }, [campaignId, campaigns.data]);

  const submitImport = useMutation({
    mutationFn: sharedApi.submitImportSubmission,
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: t('connectivity.submit-success'),
        position: 'bottom-right',
      });
    },
    onError: () => {
      notifications.show({
        color: 'red',
        message: t('connectivity.submit-error'),
        position: 'bottom-right',
      });
    },
  });

  return (
    <Stack>
      <Paper p="md">
        <Stack gap="md">
          <Title order={2}>{t('connectivity.import-title')}</Title>
          <Alert color="blue" title={t('connectivity.review-mode-title')}>
            {t('connectivity.review-mode-description')}
          </Alert>
          {campaignYear ? (
            <Text c="dimmed" size="sm">
              {t('connectivity.import-campaign', { year: campaignYear })}
            </Text>
          ) : null}
          <Group>
            <Button
              color={submitImport.isSuccess ? 'pf-green' : undefined}
              loading={submitImport.isPending}
              onClick={() => {
                submitImport.reset();
                actions.open();
              }}
              rightSection={submitImport.isSuccess ? <IconCheck /> : null}
            >
              {t('connectivity.os-import-button')}
            </Button>
          </Group>
        </Stack>
      </Paper>

      <OsImportForm
        loading={submitImport.isPending}
        onClose={actions.close}
        onSubmit={(values) => {
          submitImport.mutate({
            ...values,
            campaign_year: campaignYear,
          });
        }}
        opened={opened}
      />
    </Stack>
  );
}
